/**
 * Created by shmatul.
 * Aspect Ratios Transformations Controller
 */
import app from 'app';
import Marionette from 'backbone.marionette';
import { RatioChange_AutoCrop } from '@wochitnpm/adjustasset';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import _ from 'lodash-extended';

export const AspectRatiosController = Marionette.Object.extend(
  {
    initialize: function (options) {
      this.model = options.model;

      this.canvas = {
        width: app.ASSET_EDITOR_WIDTH,
        height: app.ASSET_EDITOR_HEIGHT,
      };

      this.setSurface();
    },

    setNewAspectRatio(id) {
      return new Promise((resolve, reject) => {
        const curId = this.model.get('aspectRatio');
        const arObj = app.ASPECT_RATIOS[id];
        if (!id || _.isEmpty(arObj)) {
          return;
        }

        this._checkCompatibility(id, arObj).then(() => {
          app.remoteStash.set('lastAspectRatio', id);
          this.model.set('aspectRatio', id, {});

          app.analytics('analytics:aspectRatioView:change', {
            'Old value': app.ASPECT_RATIOS[curId]?.friendlyVal,
            'New value': app.ASPECT_RATIOS[id]?.friendlyVal,
          });

          resolve(id);
        }, reject);
      });
    },

    _checkCompatibility(id, arObj) {
      return new Promise((resolve, reject) => {
        if (this.checkStoryDisabledCustomOverlays(id)) {
          app.dialog
            .error({
              type: app.dialog.types.ERROR_3,
              title: app.culture.confirm.coConflict.title,
              description: `Some of the effects used in this video<br>are not supported in aspect ratio ${arObj.friendlyVal}`,
              showContactCsm: false,
              buttonYesText: 'OK',
            })
            .then((res) => {
              if (res.action) {
                resolve(id);
              } else {
                reject();
              }
            }, reject);
        } else {
          resolve(id);
        }
      });
    },

    /* used to check if story will have disabled custom overlays, based on new given aspect ratio */
    checkStoryDisabledCustomOverlays: function (newAspectRatio) {
      const timelineData = this.model.getTimelineData();
      const availableThemes = this.model.get('_themeInfo');
      const storyCustomOverlays = this.model.get('customOverlays');
      const themeInfo = _.isArray(availableThemes)
        ? _.head(_.filter(availableThemes, { aspectRatio: newAspectRatio }))
        : false;

      let hasDisabledCustomOverlays = false;
      let hasDisabledStoryCustomOverlays = false;

      _.each(timelineData.visualTracks, (visualTrack) => {
        if (visualTrack.isSOTPlaceholder) {
          return;
        }
        _.each(visualTrack.agd.customOverlays, (o) => {
          if (!hasDisabledCustomOverlays) {
            hasDisabledCustomOverlays = _.isEmpty(
              _.find(themeInfo.presets, { id: o.id })
            );
          }
        });
      });

      if (storyCustomOverlays?.length) {
        _.each(storyCustomOverlays, (o) => {
          if (!hasDisabledStoryCustomOverlays) {
            hasDisabledStoryCustomOverlays = _.isEmpty(
              _.find(themeInfo.storyPresets, { id: o.id })
            );
          }
        });
      }

      return hasDisabledCustomOverlays || hasDisabledStoryCustomOverlays;
    },

    setSurface: function () {
      this.aspectRatio = this.model.get('_aspectRatio');
      this.prevAspectRatio = this.model.previous('_aspectRatio');

      const newWidth = this.canvas.height * this.aspectRatio.ratio;

      this.visibleArea = {
        curtain: Math.round((this.canvas.width - newWidth) / 2),
        width: newWidth,
        height: this.canvas.height,
      };
      this.curtainWidth = (this.canvas.width - this.visibleArea.width) / 2;

      if (this.prevAspectRatio) {
        const prevWidth = this.canvas.height * this.prevAspectRatio.ratio;
        this.prevVisibleArea = {
          curtain: Math.round((this.canvas.width - prevWidth) / 2),
          width: this.canvas.height * this.prevAspectRatio.ratio,
          height: this.canvas.height,
        };
      }
    },

    adjustVisualTrack: function ({ visualTrack, oldWidth, oldHeight }) {
      const AdjustAssetInstance = RatioChange_AutoCrop({
        innerRatio: {
          height: oldHeight,
          width: oldWidth,
        },
      });
      let actualAssetDims;
      const assetAspectRatio =
        visualTrack.metadata.width / visualTrack.metadata.height;
      const canvasAR = this.canvas.width / this.canvas.height;

      if (assetAspectRatio > canvasAR) {
        // panoramic assets are sized according to canvas width
        actualAssetDims = {
          width: this.canvas.width * (visualTrack.agd.scale || 1),
          height:
            (this.canvas.width * (visualTrack.agd.scale || 1)) /
            assetAspectRatio,
        };
      } else {
        actualAssetDims = {
          width:
            visualTrack.metadata.width *
            ((this.canvas.height * (visualTrack.agd.scale || 1)) /
              visualTrack.metadata.height),
          height: this.canvas.height * (visualTrack.agd.scale || 1),
        };
      }

      const offset = visualTrack.agd.offset;
      if (_.isObject(offset)) {
        actualAssetDims.top = offset.top * this.canvas.height;
        actualAssetDims.left = offset.left * this.canvas.width;
      }

      this._adjustCustomOverlays(visualTrack);
      this._adjustZoom(visualTrack, actualAssetDims); // keep zoom before offset
      this._adjustOffset(visualTrack, actualAssetDims);
      this._adjustTextOverlays(visualTrack);
      PictureInPictureAdapter.ensureAnimationCutEx({ scene: visualTrack });
      const { scale: AdjustScale = null, offset: adjustOffset = null } =
        AdjustAssetInstance.AdjustAsset({
          originalRatio: {
            height: visualTrack.metadata.height,
            width: visualTrack.metadata.width,
          },
          innerRatio: {
            ...this.visibleArea,
          },
          baseRatio: {
            height: app.ASSET_EDITOR_HEIGHT,
            width: app.ASSET_EDITOR_WIDTH,
          },
          agd: {
            scale: visualTrack.agd.scale,
            offset: visualTrack.agd.offset,
          },
        });
      visualTrack.agd.scale = AdjustScale;
      visualTrack.agd.offset = adjustOffset;
    },

    _adjustCustomOverlays: function (visualTrack) {
      const themeInfo = this.model.getThemeInfo();
      const prevVisibleArea = this.prevVisibleArea;
      const visibleArea = this.visibleArea;
      const canvasWidth = this.canvas.width;

      _.each(visualTrack.agd.customOverlays, function (o) {
        const samePresetInNewAr = _.find(themeInfo.presets, { id: o.id });
        o.disabled = _.isEmpty(samePresetInNewAr);
        if (!o.disabled) {
          _.each(samePresetInNewAr.fields, (field) => {
            if (
              field.pointRelativeToAspectRatio &&
              (field.type === 'AREA' || field.type === 'POINT')
            ) {
              try {
                const fromAgd = _.find(o.fields, { name: field.name });
                if (fromAgd && fromAgd.value?.trim().length) {
                  let [offsetX, offsetY, width, height, zIndex] =
                    fromAgd.value.split(' ');
                  offsetX = parseFloat(offsetX);
                  offsetY = parseFloat(offsetY);
                  width = parseFloat(width) || 0;
                  height = parseFloat(height) || 0;
                  zIndex = parseFloat(zIndex) || 0;

                  const distanceFromCenter = 0.5 - offsetX;
                  const arChangRatio =
                    visibleArea.width / prevVisibleArea.width;
                  const newDistanceFromCenter =
                    distanceFromCenter * arChangRatio;
                  let newOffsetX = 0.5 - newDistanceFromCenter;

                  const maxX =
                    (visibleArea.curtain + visibleArea.width) / canvasWidth;
                  const minX = visibleArea.curtain / canvasWidth;
                  if (newOffsetX + width > maxX) {
                    newOffsetX = maxX - width;
                  } else if (newOffsetX < minX) {
                    newOffsetX = minX;
                  }

                  if (field.type === 'POINT') {
                    fromAgd.value = [newOffsetX, offsetY].join(' ').trim();
                  } else {
                    fromAgd.value = [newOffsetX, offsetY, width, height, zIndex]
                      .join(' ')
                      .trim();
                  }
                }
              } catch (e) {
                app.doctorController.report(
                  'failed transitioning area/point field aspect ratio',
                  e
                );
              }
            }
            if (field.type === 'MEDIA' && field.relativeAspect) {
              const fromAgd = _.find(o.fields, { name: field.name });
              if (fromAgd) {
                const asObject = JSON.parse(fromAgd.value);
                if (_.isEmpty(asObject) || !_.isDefined(asObject.url)) {
                  return;
                }
                const AdjustOverlay = RatioChange_AutoCrop();
                const { scale: overlayScale, offset: overlayOffset } =
                  AdjustOverlay.AdjustAssetPositionBase({
                    assetPosition: field.relativeAspect,
                    baseAspectRatio: {
                      width: app.ASSET_EDITOR_WIDTH,
                      height: app.ASSET_EDITOR_HEIGHT,
                    },
                    asset: {
                      metadata: {
                        width: asObject.width,
                        height: asObject.height,
                      },
                      agd: {
                        offset: {
                          left: asObject.cropOffsetX,
                          top: asObject.cropOffsetY,
                        },
                        scale: asObject.cropScale,
                      },
                    },
                  });
                asObject.cropOffsetX = overlayOffset?.left;
                asObject.cropOffsetY = overlayOffset?.top;
                asObject.cropScale = overlayScale || 1;
                fromAgd.value = JSON.stringify(asObject);
              }
            }
          });
        }
      });
    },

    _adjustZoom: function (visualTrack, actualAssetDims) {
      if (app.imageEffectType.isZOOM(visualTrack)) {
        const zoomAdj = {
          x: visualTrack.agd.zoomX,
          y: visualTrack.agd.zoomY,
          x1: visualTrack.agd.zoomX1,
          y1: visualTrack.agd.zoomY1,
        };

        zoomAdj.width = (zoomAdj.x1 - zoomAdj.x) * actualAssetDims.width;
        zoomAdj.height = (zoomAdj.y1 - zoomAdj.y) * actualAssetDims.height;
        zoomAdj.zoomRatio = zoomAdj.width / zoomAdj.height;
        zoomAdj.centerX = zoomAdj.x + (zoomAdj.x1 - zoomAdj.x) / 2;
        zoomAdj.centerY = zoomAdj.y + (zoomAdj.y1 - zoomAdj.y) / 2;

        if (this.aspectRatio.ratio >= 1 && zoomAdj.cropRatio >= 1) {
          zoomAdj.newHeight = zoomAdj.height;
          zoomAdj.newWidth = zoomAdj.newHeight * this.aspectRatio.ratio;
        } else if (this.aspectRatio.ratio >= 1 && zoomAdj.cropRatio < 1) {
          zoomAdj.newWidth = zoomAdj.height * 1.05;
          zoomAdj.newHeight = zoomAdj.newWidth / this.aspectRatio.ratio;
        } else {
          zoomAdj.newHeight = zoomAdj.width * 1.05;
          zoomAdj.newWidth = zoomAdj.newHeight * this.aspectRatio.ratio;
        }

        if (zoomAdj.newWidth > this.visibleArea.width) {
          zoomAdj.newWidth = this.visibleArea.width * 0.9;
          zoomAdj.newHeight = zoomAdj.newWidth / this.aspectRatio.ratio;
        }

        if (zoomAdj.newHeight > this.visibleArea.height) {
          zoomAdj.newHeight = this.visibleArea.height * 0.9;
          zoomAdj.newWidth = zoomAdj.newHeight * this.aspectRatio.ratio;
        }

        zoomAdj.newWidthPercent = zoomAdj.newWidth / actualAssetDims.width;
        zoomAdj.newHeightPercent = zoomAdj.newHeight / actualAssetDims.height;

        zoomAdj.newX = zoomAdj.centerX - zoomAdj.newWidthPercent / 2;
        zoomAdj.newY = zoomAdj.centerY - zoomAdj.newHeightPercent / 2;

        if (zoomAdj.newX < 0) {
          zoomAdj.newX = 0;
        }

        if (zoomAdj.newY < 0) {
          zoomAdj.newY = 0;
        }

        zoomAdj.newX1 = zoomAdj.newX + zoomAdj.newWidthPercent;
        zoomAdj.newY1 = zoomAdj.newY + zoomAdj.newHeightPercent;

        if (zoomAdj.newX1 > 1) {
          zoomAdj.newX1 = 1;
          zoomAdj.newX = zoomAdj.newX1 - zoomAdj.newWidthPercent;
        }

        if (zoomAdj.newY1 > 1) {
          zoomAdj.newY1 = 1;
          zoomAdj.newY = zoomAdj.newY1 - zoomAdj.newHeightPercent;
        }

        visualTrack.agd.zoomX = zoomAdj.newX;
        visualTrack.agd.zoomY = zoomAdj.newY;
        visualTrack.agd.zoomX1 = zoomAdj.newX1;
        visualTrack.agd.zoomY1 = zoomAdj.newY1;
      }
    },

    _adjustOffset: function (visualTrack, actualAssetDims) {
      const newAssetProperties = { left: null, top: null };
      const workAssetDim = $.extend({ left: 0, top: 0 }, actualAssetDims);
      /* eslint-disable no-unused-vars */
      let isBiggerLeft;
      let isSmallerRight;
      let shouldCenter;
      /* eslint-enable no-unused-vars */
      let zoomAdj = {};

      if (!workAssetDim.width && _.isObject(visualTrack.agd.offset)) {
        // tweets don't have dimensions, so resize the offset
        delete visualTrack.agd.offset;
        visualTrack.agd.scale = 1;
      } else if (
        _.isObject(visualTrack.agd.offset) ||
        app.imageEffectType.isZOOM(visualTrack)
      ) {
        if (app.imageEffectType.isZOOM(visualTrack)) {
          // zoom surface should be the center of the visible area
          zoomAdj = {
            x: visualTrack.agd.zoomX * workAssetDim.width,
            y: visualTrack.agd.zoomY * workAssetDim.height,
            x1: visualTrack.agd.zoomX1 * workAssetDim.width,
            y1: visualTrack.agd.zoomY1 * workAssetDim.height,
          };
          zoomAdj.width = zoomAdj.x1 - zoomAdj.x;
          zoomAdj.height = zoomAdj.y1 - zoomAdj.y;

          if (
            workAssetDim.width > this.visibleArea.width &&
            (zoomAdj.x + workAssetDim.left + workAssetDim.width <
              this.curtainWidth ||
              zoomAdj.x1 + workAssetDim.left + workAssetDim.width >
                this.curtainWidth + this.visibleArea.width)
          ) {
            workAssetDim.left = newAssetProperties.left =
              this.curtainWidth -
              zoomAdj.x +
              (this.visibleArea.width - zoomAdj.width) / 2;
          }
        }

        // isBiggerLeft = workAssetDim.left > this.curtainWidth;
        // isSmallerRight = workAssetDim.width + workAssetDim.left <
        //     this.curtainWidth + this.visibleArea.width;
        // shouldCenter = workAssetDim.width < this.visibleArea.width &&
        //     (isBiggerLeft || isSmallerRight);

        // if (isBiggerLeft && workAssetDim.width >= this.visibleArea.width) {
        //   newAssetProperties.left = this.curtainWidth;
        // } else if (isSmallerRight && workAssetDim.width >=
        //     this.visibleArea.width) {
        //   newAssetProperties.left = this.curtainWidth + this.visibleArea.width -
        //       workAssetDim.width;
        // }

        // if (shouldCenter) {
        //   if (!workAssetDim.top) {
        //     workAssetDim.offset = null;
        //     visualTrack.agd.offset = null;
        //   } else {
        //     newAssetProperties.left = (this.canvas.width / 2) -
        //         (workAssetDim.width / 2);
        //   }
        // }

        // if (!_.isNull(newAssetProperties.left)) {
        //   visualTrack.agd.offset = {
        //     left: newAssetProperties.left / this.canvas.width,
        //     // normalize other values (must be available if any offset / scale is on)
        //     top: (visualTrack.agd.offset) ? visualTrack.agd.offset.top : 0,
        //     scale: visualTrack.agd.scale || 1,
        //   };
        // }
      }
    },

    _repositionBox: function (to) {
      const heightInPixels = Math.round(to.height * this.canvas.height);
      const widthInPixels = Math.round(to.width * this.canvas.width);
      const originalScale = app.sfp(
        this.visibleArea.width / this.prevVisibleArea.width
      );
      let scaleRatio = app.sfp(
        Math.min(
          this.visibleArea.width / this.prevVisibleArea.width,
          this.canvas.height / heightInPixels
        )
      );
      if (this.aspectRatio.ratio > this.prevAspectRatio.ratio) {
        scaleRatio = 1;
      }

      const oldOffsetXInPixels = Math.round(to.offsetX * this.canvas.width);
      const oldOffsetXInPixelsRelativeToVisibleArea =
        oldOffsetXInPixels - this.prevVisibleArea.curtain;
      const offsetXInPercentRelativeToVisibleArea = app.sfp(
        oldOffsetXInPixelsRelativeToVisibleArea / this.prevVisibleArea.width
      );
      const newOffsetXInPixelsRelativeToVisibleArea = Math.round(
        offsetXInPercentRelativeToVisibleArea * this.visibleArea.width
      );
      let newOffsetXInPixels =
        newOffsetXInPixelsRelativeToVisibleArea + this.visibleArea.curtain;

      let offsetYInPixels = Math.round(to.offsetY * this.canvas.height);
      const isBottomHalf =
        offsetYInPixels + heightInPixels / 2 > this.canvas.height / 2;
      const isRightHalf =
        oldOffsetXInPixelsRelativeToVisibleArea + widthInPixels / 2 >
        this.prevVisibleArea.width / 2;
      const newHeight = app.sfp(to.height * scaleRatio);
      const newWidth = app.sfp(to.width * scaleRatio);
      const newHeightInPixels = Math.round(newHeight * this.canvas.height);
      const newWidthInPixels = Math.round(
        to.width * scaleRatio * this.canvas.width
      );
      const newWidthInPixels1 = to.width * originalScale * this.canvas.width;
      const offsetFix = {
        x: 0,
        y: 0,
      };

      if (isBottomHalf) {
        offsetFix.y = heightInPixels - newHeightInPixels;
        offsetYInPixels += offsetFix.y;
      }
      if (isRightHalf) {
        offsetFix.x = newWidthInPixels1 - widthInPixels;
        newOffsetXInPixels += newWidthInPixels1 - newWidthInPixels;
      }

      // var offsetFix.y = offsetYInPixels + newHeightInPixels - this.canvas.height;

      to.width = newWidth;
      to.height = newHeight;
      to.fontSize = to.fontSize * scaleRatio;
      to.offsetX = app.sfp(newOffsetXInPixels / this.canvas.width);
      to.offsetY = app.sfp(offsetYInPixels / this.canvas.height);

      return {
        offsetFix: offsetFix,
        scaleRatio: scaleRatio,
      };
    },

    _repositionToken: function (
      token,
      offsetFix,
      scaleRatio,
      po,
      newTo,
      oldTo
    ) {
      const fontSize = parseFloat(token.style['font-size']);
      po = _.extend(
        {
          topBottom: 0,
          topBottomNew: 0,
          leftRight: 0,
          leftRightNew: 0,
        },
        po
      );

      // translate offsetX

      const oldTextOverlayLayOffsetXinPixels =
        oldTo.offsetX * this.canvas.width;
      const newTextOverlayLayOffsetXinPixels =
        newTo.offsetX * this.canvas.width;
      const oldToWidthInPixels = oldTo.width * this.canvas.width;
      const newToWidthInPixels = newTo.width * this.canvas.width;

      const oldOffsetXInPixels = token.left * this.canvas.width;
      const oldOffsetXInPixelsRelativeToTextOverlay =
        oldOffsetXInPixels - oldTextOverlayLayOffsetXinPixels;
      const offsetXRelativeToTextOverlay =
        oldOffsetXInPixelsRelativeToTextOverlay / oldToWidthInPixels;
      const newOffsetXInPixelsRelativeToTextOverlay =
        offsetXRelativeToTextOverlay * newToWidthInPixels;
      const newOffsetXInPixels =
        newOffsetXInPixelsRelativeToTextOverlay +
        newTextOverlayLayOffsetXinPixels;

      // translate width
      const oldWidthInPixels = (token.right - token.left) * this.canvas.width;
      const widthRelativeToTextOverlay = app.sfp(
        oldWidthInPixels / oldToWidthInPixels
      );
      const newWidthInPixels = widthRelativeToTextOverlay * newToWidthInPixels;

      // translate offsetY

      const oldTextOverlayLayOffsetYinPixels =
        oldTo.offsetY * this.canvas.height;
      const newTextOverlayLayOffsetYinPixels =
        newTo.offsetY * this.canvas.height;
      const oldToHeightInPixels = oldTo.height * this.canvas.height;
      const newToHeightInPixels = newTo.height * this.canvas.height;

      const oldOffsetYInPixels = token.top * this.canvas.height;
      const oldOffsetYInPixelsRelativeToTextOverlay =
        oldOffsetYInPixels - oldTextOverlayLayOffsetYinPixels;
      const offsetYRelativeToTextOverlay =
        oldOffsetYInPixelsRelativeToTextOverlay / oldToHeightInPixels;
      const newOffsetYInPixelsRelativeToTextOverlay =
        offsetYRelativeToTextOverlay * newToHeightInPixels;
      const newOffsetYInPixels =
        newOffsetYInPixelsRelativeToTextOverlay +
        newTextOverlayLayOffsetYinPixels;

      // translate height
      const oldHeightInPixels = (token.bottom - token.top) * this.canvas.height;
      const heightRelativeToTextOverlay = app.sfp(
        oldHeightInPixels / oldToHeightInPixels
      );
      const newHeightInPixels =
        heightRelativeToTextOverlay * newToHeightInPixels;

      token.left = app.sfp(newOffsetXInPixels / this.canvas.width);
      token.top = app.sfp(newOffsetYInPixels / this.canvas.height);
      token.right = token.left + app.sfp(newWidthInPixels / this.canvas.width);
      token.bottom =
        token.top + app.sfp(newHeightInPixels / this.canvas.height);
      token.style['font-size'] = fontSize * scaleRatio + 'px';
    },

    _getPaddingOffset: function (to, scale) {
      const oldPadding = to.padding.split(/\s/);
      const tb = parseFloat(oldPadding[0]);
      const lr = parseFloat(oldPadding[1]);
      const tbNew = tb * scale;
      const lrNew = lr * scale;
      to.padding = tbNew + 'px ' + lrNew + 'px';

      return {
        topBottom: tb,
        leftRight: lr,
        topBottomNew: tbNew,
        leftRightNew: lrNew,
      };
    },

    _repositionTextOverlay: function (to) {
      if (!to) {
        return;
      }

      const oldTo = _.extend({}, to);

      const res = this._repositionBox(to);
      const po = this._getPaddingOffset(to, res.scaleRatio);

      _.each(
        to.paragraphs,
        function (p) {
          _.each(
            p.tokens,
            function (token) {
              this._repositionToken(
                token,
                res.offsetFix,
                res.scaleRatio,
                po,
                to,
                oldTo
              );
            }.bind(this)
          );
        }.bind(this)
      );
    },

    _adjustTextOverlays: function (visualTrack) {
      _.each(
        visualTrack.agd.textOverlays,
        function (to) {
          this._repositionTextOverlay(to);
        }.bind(this)
      );
    },
  },

  // static methods:
  {
    /**
     * get first theme by priority
     * @param themes - organization brand themes
     * @returns {*|null}
     */
    getPrioritizedAspectRatio(themes) {
      const AR_CLONE_PRIORITIES = [
        'ASPECT_16_9',
        'ASPECT_1_1',
        'ASPECT_9_16',
        'ASPECT_4_5',
        'ASPECT_3_4',
        'ASPECT_3_2',
      ];

      for (const ar of AR_CLONE_PRIORITIES) {
        for (const theme of themes) {
          if (theme.aspectRatio === ar) {
            return theme;
          }
        }
      }

      return null;
    },

    dropSupportForAspectRatio(sAspectRatio) {
      if (app.ASPECT_RATIOS[sAspectRatio]) {
        delete app.ASPECT_RATIOS[sAspectRatio];
      }
    },
  }
);
